import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type DateRange, DateRangeSelector } from '@components/DateRangeSelector';
import { MODAL_TYPES } from '@components/modals/constants';
import PatientPicker from '@components/Patient/PatientPicker';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { type Clinician, useClinicians } from '@features/organizations';
import { useTrackEvent } from '@jotpsych/statsig';
import { Avatar, ProviderBadge } from '@jotpsych/ui';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BlockIcon from '@mui/icons-material/Block';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOffOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import SendIcon from '@mui/icons-material/Send';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { EventNames } from '@services/statsig/constants';
import { useModal } from '@store/hooks';

import type { Patient, PatientInfo } from '../../../../../types/types';
import type {
  UnifiedEncounterClaimProblem,
  UnifiedEncounterNextStep,
  UnifiedEncounterStage,
  UnifiedEncounterWorklistItem,
  UnifiedEncounterWorklistSegment,
  UnifiedEncounterWorkState,
} from '../../../store';
import { useLazyGetUnifiedEncounterWorklistQuery } from '../../../store';
import { workbenchProvenanceState } from '../../../utils/worklistProvenance';
import { EncounterIdentityBadges } from '../redesign';
import {
  formatCents,
  formatCentsCompact,
  Pagination,
  PayerFilter,
  useBillingPayerOptionsQuery,
  useClaimDisposition,
} from '../shared';

type StageState = 'complete' | 'current' | 'ready' | 'waiting' | 'blocked' | 'future';
type IndicatorTone = 'green' | 'amber' | 'red' | 'gray' | 'midnight';
type ChipColor = 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';
type SortBy = 'dos' | 'activity';
type SortOrder = 'asc' | 'desc';
type SortPreset = `${SortBy}:${SortOrder}`;
type ClaimFilter = 'any' | 'has_claim' | 'no_claim';
type MoneyTextTone = 'default' | 'muted' | 'success' | 'warning' | 'error' | 'info';
type WorklistMoney = UnifiedEncounterWorklistItem['money'];
type WorklistMoneyReconciliation = NonNullable<WorklistMoney['reconciliation']>;
type WorklistMoneySecondary = NonNullable<WorklistMoney['secondary']>;
type RowOpenEvent =
  | React.MouseEvent<HTMLElement>
  | React.KeyboardEvent<HTMLElement>;

interface WorklistFilterState {
  workState: UnifiedEncounterWorkState;
  stage: UnifiedEncounterStage;
  sortBy: SortBy;
  sortOrder: SortOrder;
  dateRange: DateRange;
  patientId: string;
  patientLabel: string;
  providerIds: string[];
  payerUuid: string;
  payerName: string;
  owner: string;
  nextStep: '' | UnifiedEncounterNextStep;
  claimProblem: '' | UnifiedEncounterClaimProblem;
  claimFilter: ClaimFilter;
  staleOnly: boolean;
  billWithJotpsych: '' | 'yes' | 'no';
  perPage: number;
}

const STAGES: Array<{
  key: Exclude<UnifiedEncounterStage, 'all'>;
  label: string;
  filterLabel: string;
  shortLabel: string;
  icon: React.ReactElement;
}> = [
  { key: 'schedule', label: 'Scheduled', filterLabel: 'Schedule', shortLabel: 'Sched', icon: <CalendarMonthIcon fontSize="small" /> },
  { key: 'support', label: 'Supported', filterLabel: 'Support', shortLabel: 'Support', icon: <FactCheckIcon fontSize="small" /> },
  { key: 'sign', label: 'Signed', filterLabel: 'Sign', shortLabel: 'Signed', icon: <AssignmentTurnedInIcon fontSize="small" /> },
  { key: 'submit', label: 'Submitted', filterLabel: 'Submit', shortLabel: 'Submit', icon: <SendIcon fontSize="small" /> },
  { key: 'settle', label: 'Settled', filterLabel: 'Settle', shortLabel: '$', icon: <LocalAtmIcon fontSize="small" /> },
];

const stageRank = STAGES.reduce<Record<string, number>>(
  (acc, stage, index) => ({ ...acc, [stage.key]: index }),
  {},
);

const ownerColor: Record<string, ChipColor> = {
  front_desk: 'info',
  clinician: 'warning',
  biller: 'primary',
  billing: 'primary',
  payer: 'default',
  patient: 'warning',
  none: 'success',
};

const signalColor: Record<UnifiedEncounterWorkState, ChipColor> = {
  all: 'default',
  action: 'warning',
  waiting: 'info',
  clear: 'success',
};

const moneyTextToneSx: Record<
  MoneyTextTone,
  { color: string; labelColor: string }
> = {
  default: {
    color: 'text.primary',
    labelColor: 'text.secondary',
  },
  muted: {
    color: 'text.secondary',
    labelColor: 'text.secondary',
  },
  success: {
    color: 'success.dark',
    labelColor: 'success.dark',
  },
  warning: {
    color: 'warning.dark',
    labelColor: 'warning.dark',
  },
  error: {
    color: 'error.dark',
    labelColor: 'error.dark',
  },
  info: {
    color: 'info.dark',
    labelColor: 'info.dark',
  },
};

const toneColor: Record<IndicatorTone, string> = {
  green: ELIG_TOKENS.green,
  amber: ELIG_TOKENS.amber,
  red: ELIG_TOKENS.red,
  gray: ELIG_TOKENS.gray,
  midnight: ELIG_TOKENS.midnight,
};

const worklistGridTemplateColumns = [
  '36px',
  'minmax(88px, 0.65fr)',
  'minmax(160px, 1.15fr)',
  'minmax(140px, 0.9fr)',
  'minmax(150px, 1fr)',
  'minmax(150px, 1.05fr)',
  'minmax(196px, 1.65fr)',
  'minmax(174px, 0.95fr)',
  '40px',
].join(' ');

const worklistHeaderCells = [
  { key: 'dos', label: 'DOS' },
  { key: 'patient', label: 'Patient' },
  { key: 'service', label: 'Service' },
  { key: 'provider', label: 'Provider' },
  { key: 'flow', label: 'Flow' },
  { key: 'work', label: 'Work' },
  { key: 'money', label: 'Money', align: 'right' as const },
];

const emptyWorklistRows: UnifiedEncounterWorklistItem[] = [];
const defaultSkeletonRowCount = 6;
const worklistFilterStorageKey = 'jotbill.unifiedEncounterWorklist.filters.v1';
const toolbarControlHeight = 36;
const toolbarFieldSx = {
  height: toolbarControlHeight,
  '& .MuiInputBase-root': {
    height: toolbarControlHeight,
    fontSize: '0.8125rem',
  },
  '& .MuiInputBase-input': {
    py: 0,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    py: 0,
  },
};
const toolbarButtonSx = {
  minHeight: toolbarControlHeight,
  height: toolbarControlHeight,
  textTransform: 'none',
};
const drawerFieldSx = {
  '& .MuiInputBase-root': {
    minHeight: 40,
  },
};
const defaultWorklistFilters: WorklistFilterState = {
  workState: 'all',
  stage: 'all',
  sortBy: 'activity',
  sortOrder: 'desc',
  dateRange: { start: null, end: null },
  patientId: '',
  patientLabel: '',
  providerIds: [],
  payerUuid: '',
  payerName: '',
  owner: '',
  nextStep: '',
  claimProblem: '',
  claimFilter: 'any',
  staleOnly: false,
  billWithJotpsych: '',
  perPage: 25,
};

const sortOptions: Array<{
  value: SortPreset;
  label: string;
  sortBy: SortBy;
  sortOrder: SortOrder;
}> = [
  { value: 'activity:desc', label: 'Latest activity', sortBy: 'activity', sortOrder: 'desc' },
  { value: 'activity:asc', label: 'Oldest activity', sortBy: 'activity', sortOrder: 'asc' },
  { value: 'dos:desc', label: 'Newest service date', sortBy: 'dos', sortOrder: 'desc' },
  { value: 'dos:asc', label: 'Oldest service date', sortBy: 'dos', sortOrder: 'asc' },
];
const defaultSortPreset: SortPreset = 'activity:desc';

const ownerOptions = [
  { value: 'front_desk', label: 'Front desk' },
  { value: 'clinician', label: 'Clinician' },
  { value: 'biller', label: 'Biller' },
  { value: 'billing', label: 'Billing' },
  { value: 'patient', label: 'Patient' },
  { value: 'payer', label: 'Payer' },
];

/**
 * Human-readable labels for the generated next-step vocabulary.
 *
 * Keyed by the generated `UnifiedEncounterNextStep` union, so adding or
 * renaming a backend next step fails typecheck here until the label is
 * updated — the option list below is derived from this map rather than
 * hand-maintained alongside it.
 */
const nextStepLabels: Record<UnifiedEncounterNextStep, string> = {
  monitor_visit: 'Monitor visit',
  verify_coverage: 'Verify coverage',
  sign_note: 'Sign note',
  create_claim: 'Create claim',
  review_claim: 'Review claim',
  track_payer: 'Track payer',
  send_invoice: 'Send invoice',
  collect_balance: 'Collect balance',
  track_settlement: 'Track settlement',
  done: 'Done',
};

const nextStepOptions: Array<{ value: UnifiedEncounterNextStep; label: string }> = (
  Object.keys(nextStepLabels) as UnifiedEncounterNextStep[]
).map(value => ({ value, label: nextStepLabels[value] }));

const claimProblemLabels: Record<UnifiedEncounterClaimProblem, string> = {
  any_problem: 'Any claim problem',
  rejected_or_denied: 'Rejected or denied',
  rejected: 'Rejected',
  denied: 'Denied',
  scrub_failed: 'Scrub failed',
  reversal_or_recoupment: 'Reversed or recouped',
  unmatched_remittance: 'Unmatched remittance',
};

const claimProblemOptions: Array<{
  value: UnifiedEncounterClaimProblem;
  label: string;
}> = (Object.keys(claimProblemLabels) as UnifiedEncounterClaimProblem[]).map(value => ({
  value,
  label: claimProblemLabels[value],
}));

interface MoneySlotSpec {
  key: string;
  icon: React.ReactElement;
  value: string;
  tone?: MoneyTextTone;
  tooltip: string;
}

export const UnifiedEncounterWorklistTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { open: openModal } = useModal();
  const { trackEvent } = useTrackEvent();
  const { billing: billingTracking } = useEventTracking();
  const { clinicians, isLoading: cliniciansLoading } = useClinicians();
  const { data: payerStatsData, isLoading: payersLoading } = useBillingPayerOptionsQuery();
  const [filters, setFilters] = useState<WorklistFilterState>(() => loadWorklistFilters());
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [patientPickerKey, setPatientPickerKey] = useState(0);
  const activeWorklistRequestRef = useRef<{ abort: () => void } | null>(null);

  const dateFrom = toISODate(filters.dateRange.start);
  const dateTo = toISODate(filters.dateRange.end);
  const advancedFilterCount = getAdvancedFilterCount(filters);
  const hasAnyFilter = hasNonDefaultFilters(filters);

  const queryArgs = useMemo(
    () => ({
      workState: filters.workState,
      stage: filters.stage,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      patientId: filters.patientId || undefined,
      providerIds: filters.providerIds.length ? filters.providerIds : undefined,
      payerUuid: filters.payerUuid || undefined,
      dateFrom,
      dateTo,
      owner: filters.owner || undefined,
      nextStep: filters.nextStep || undefined,
      claimProblem: filters.claimProblem || undefined,
      hasClaim: filters.claimFilter === 'any' ? undefined : filters.claimFilter === 'has_claim',
      staleOnly: filters.staleOnly || undefined,
      billWithJotpsych: filters.billWithJotpsych === 'yes' ? true : filters.billWithJotpsych === 'no' ? false : undefined,
      page,
      perPage: filters.perPage,
    }),
    [dateFrom, dateTo, filters, page],
  );

  const [
    fetchWorklist,
    { data, isFetching, isLoading, isUninitialized },
  ] = useLazyGetUnifiedEncounterWorklistQuery();

  const rows = data?.items ?? emptyWorklistRows;
  const isWorklistLoading = isUninitialized || isLoading || isFetching;
  const skeletonRowCount = Math.max(
    defaultSkeletonRowCount,
    Math.min(rows.length || defaultSkeletonRowCount, filters.perPage),
  );

  const updateFilters = useCallback((patch: Partial<WorklistFilterState>) => {
    setFilters(current => {
      const next = { ...current, ...patch };
      saveWorklistFilters(next);
      return next;
    });
    setPage(1);
    setSelectedIds(new Set());
  }, []);

  const trackClaimProblemChange = useCallback(
    (claimProblem: WorklistFilterState['claimProblem']) => {
      billingTracking.worklistClaimProblemFilterChanged({
        claim_problem: claimProblem || 'none',
      });
    },
    [billingTracking],
  );

  const handleWorkStateChange = useCallback((key: string) => {
    updateFilters({ workState: key as UnifiedEncounterWorkState });
  }, [updateFilters]);

  const handleStageChange = useCallback((key: string) => {
    updateFilters({ stage: key as UnifiedEncounterStage });
  }, [updateFilters]);

  const handlePerPageChange = useCallback((next: number) => {
    updateFilters({ perPage: next });
  }, [updateFilters]);

  const handleResetView = useCallback(() => {
    if (filters.claimProblem) trackClaimProblemChange('');
    setFilters(defaultWorklistFilters);
    saveWorklistFilters(defaultWorklistFilters);
    setPage(1);
    setSelectedIds(new Set());
    setPatientPickerKey(current => current + 1);
  }, [filters.claimProblem, trackClaimProblemChange]);

  const startWorklistRequest = useCallback(() => {
    activeWorklistRequestRef.current?.abort();
    const request = fetchWorklist(queryArgs);
    activeWorklistRequestRef.current = request;
    void request
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        if (activeWorklistRequestRef.current === request) {
          activeWorklistRequestRef.current = null;
        }
      });
    return request;
  }, [fetchWorklist, queryArgs]);

  useEffect(() => {
    const request = startWorklistRequest();
    return () => {
      request.abort();
    };
  }, [startWorklistRequest]);

  const handleRefreshWorklist = useCallback(() => {
    startWorklistRequest();
  }, [startWorklistRequest]);

  const handleClearAdvancedFilters = useCallback(() => {
    if (filters.claimProblem) trackClaimProblemChange('');
    updateFilters({
      patientId: '',
      patientLabel: '',
      providerIds: [],
      payerUuid: '',
      payerName: '',
      owner: '',
      nextStep: '',
      claimProblem: '',
      claimFilter: 'any',
      staleOnly: false,
      billWithJotpsych: '',
    });
    setPatientPickerKey(current => current + 1);
  }, [filters.claimProblem, trackClaimProblemChange, updateFilters]);

  const allVisibleSelected =
    rows.length > 0 && rows.every(row => selectedIds.has(row.id));
  const someVisibleSelected = rows.some(row => selectedIds.has(row.id));

  const handleToggleAllVisible = useCallback(() => {
    setSelectedIds(current => {
      if (rows.length > 0 && rows.every(row => current.has(row.id))) {
        return new Set([...current].filter(id => !rows.some(row => row.id === id)));
      }
      return new Set([...current, ...rows.map(row => row.id)]);
    });
  }, [rows]);

  const handleToggleRow = useCallback((rowId: string) => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleCoverageGapClick = useCallback(
    (patientId: string) => {
      trackEvent(EventNames.COVERAGE_GAP_UPDATE_INSURANCE_CLICKED, {
        metadata: {
          patient_id: patientId,
          source: 'jotbill_worklist',
        },
      });
      openModal(MODAL_TYPES.INSURANCE_EDIT, { patientId });
    },
    [openModal, trackEvent],
  );

  const handleOpenRow = useCallback((row: UnifiedEncounterWorklistItem, event?: RowOpenEvent) => {
    const shouldOpenNewTab = event ? event.metaKey || event.ctrlKey : false;
    const href = row.actions.primary_href;

    if (shouldOpenNewTab) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(href);
  }, [navigate]);

  const handleOpenClaimWorkspace = useCallback((row: UnifiedEncounterWorklistItem, event?: RowOpenEvent) => {
    const shouldOpenNewTab = event ? event.metaKey || event.ctrlKey : false;
    const href = getClaimWorkspaceHref(row);

    if (shouldOpenNewTab) {
      /* A new tab has no history state to carry — the workbench's
         Claims-worklist fallback is the honest destination there. */
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    /* Provenance (GDN-3197): the workbench's back button says "Encounters"
       and returns here. This list's filters persist in localStorage, so the
       URL alone is the right return target (page-1 reset stays, decided). */
    navigate(href, {
      state: workbenchProvenanceState({
        url: `${location.pathname}${location.search}`,
        label: 'Encounters',
      }),
    });
  }, [location.pathname, location.search, navigate]);

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              sx={{
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', lg: 'center' },
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="h6">Encounter list</Typography>
                <Typography variant="body2" color="text.secondary">
                  Click any row to open the unified encounter page. Click a signal to preview the action surface.
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                  minWidth: 0,
                }}
              >
                <Box sx={{ height: toolbarControlHeight, display: 'flex', alignItems: 'center' }}>
                  <SegmentedControl
                    value={filters.workState}
                    onChange={handleWorkStateChange}
                    size="small"
                    ariaLabel="Encounter work filter"
                    options={(data?.segments ?? emptyWorkSegments).map(segment => ({
                      value: segment.key,
                      label: segment.label,
                    }))}
                  />
                </Box>
                <Box
                  sx={{
                    width: { xs: '100%', sm: 200 },
                    flex: { xs: '1 1 100%', sm: '0 0 200px' },
                    ...toolbarFieldSx,
                  }}
                >
                  <DateRangeSelector
                    value={filters.dateRange}
                    onChange={dateRange => updateFilters({ dateRange })}
                    label="Date range"
                    size="small"
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 184 }, flex: { xs: '1 1 100%', sm: '0 0 184px' } }}>
                  <Select<SortPreset>
                    aria-label="Sort encounters"
                    fullWidth
                    size="small"
                    value={toSortPreset(filters)}
                    onChange={event => {
                      const option = sortOptions.find(({ value }) => value === event.target.value);
                      if (!option) return;
                      updateFilters({
                        sortBy: option.sortBy,
                        sortOrder: option.sortOrder,
                      });
                    }}
                    sx={toolbarFieldSx}
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Badge
                  badgeContent={advancedFilterCount}
                  color="primary"
                  invisible={advancedFilterCount === 0}
                  sx={{ height: toolbarControlHeight }}
                >
                  <Button
                    variant={advancedFilterCount > 0 ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFiltersOpen(true)}
                    sx={toolbarButtonSx}
                  >
                    Filters
                  </Button>
                </Badge>
                <Tooltip title={isFetching ? 'Refreshing encounter list' : 'Refresh encounter list'}>
                  <span>
                    <IconButton
                      aria-label="Refresh encounter list"
                      size="small"
                      onClick={handleRefreshWorklist}
                      sx={{
                        width: toolbarControlHeight,
                        height: toolbarControlHeight,
                      }}
                    >
                      <RefreshOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {hasAnyFilter && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleResetView}
                    sx={toolbarButtonSx}
                  >
                    Reset view
                  </Button>
                )}
              </Box>
            </Stack>

            <StageFlowFilter
              value={filters.stage}
              onChange={handleStageChange}
            />

            {advancedFilterCount > 0 && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                {getAdvancedFilterChips(filters, clinicians).map(chip => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    variant="outlined"
                    onDelete={chip.onDelete ? () => {
                      if (chip.key === 'claimProblem') trackClaimProblemChange('');
                      chip.onDelete(updateFilters);
                      if (chip.key === 'patient') setPatientPickerKey(current => current + 1);
                    } : undefined}
                  />
                ))}
                <Button
                  size="small"
                  variant="text"
                  onClick={handleClearAdvancedFilters}
                  sx={{ textTransform: 'none' }}
                >
                  Clear filters
                </Button>
              </Stack>
            )}

            <Typography variant="body2" color="text.secondary">
              {data?.summary ?? 'Loading encounter worklist.'}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: 420 },
              p: 2.5,
            },
          },
        }}
      >
        <Stack spacing={2.5} sx={{ height: '100%' }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Filters</Typography>
              <Typography variant="body2" color="text.secondary">
                Narrow the encounter worklist.
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={handleClearAdvancedFilters}
              disabled={advancedFilterCount === 0}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
          </Stack>
          <Divider />

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">People</Typography>
            <PatientPicker
              key={patientPickerKey}
              initialPatient={getPersistedPatientOption(filters)}
              onPatientSelect={(patient: Patient | undefined) => {
                updateFilters({
                  patientId: patient?.patient_id ?? '',
                  patientLabel: patient ? getPatientDisplayName(patient) : '',
                });
              }}
              size="small"
              disableAddNew
            />
            <Autocomplete<Clinician, true>
              multiple
              size="small"
              options={clinicians}
              loading={cliniciansLoading}
              value={clinicians.filter(clinician => filters.providerIds.includes(clinician.userId))}
              onChange={(_event, value) => {
                updateFilters({ providerIds: value.map(clinician => clinician.userId) });
              }}
              getOptionLabel={option => option.name}
              isOptionEqualToValue={(option, value) => option.userId === value.userId}
              renderInput={params => <TextField {...params} label="Providers" size="small" />}
              sx={drawerFieldSx}
            />
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Billing</Typography>
            <PayerFilter
              payers={payerStatsData?.payers ?? []}
              isLoading={payersLoading}
              value={filters.payerUuid}
              onChange={(payerUuid, payerName) => updateFilters({ payerUuid, payerName })}
            />
            <FormControl size="small" fullWidth sx={drawerFieldSx}>
              <InputLabel id="encounter-claim-filter-label">Claim</InputLabel>
              <Select<ClaimFilter>
                labelId="encounter-claim-filter-label"
                label="Claim"
                value={filters.claimFilter}
                onChange={event => updateFilters({ claimFilter: event.target.value })}
              >
                <MenuItem value="any">Any claim state</MenuItem>
                <MenuItem value="has_claim">Has claim</MenuItem>
                <MenuItem value="no_claim">No claim</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Responsibility</Typography>
            <FormControl size="small" fullWidth sx={drawerFieldSx}>
              <InputLabel id="encounter-owner-filter-label">Owner</InputLabel>
              <Select
                labelId="encounter-owner-filter-label"
                label="Owner"
                value={filters.owner}
                onChange={event => updateFilters({ owner: event.target.value })}
              >
                <MenuItem value="">Any owner</MenuItem>
                {ownerOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={drawerFieldSx}>
              <InputLabel id="encounter-next-step-filter-label">Next step</InputLabel>
              <Select<WorklistFilterState['nextStep']>
                labelId="encounter-next-step-filter-label"
                label="Next step"
                value={filters.nextStep}
                onChange={event => updateFilters({ nextStep: event.target.value })}
              >
                <MenuItem value="">Any next step</MenuItem>
                {nextStepOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={drawerFieldSx}>
              <InputLabel id="encounter-claim-problem-filter-label">Claim problem</InputLabel>
              <Select<WorklistFilterState['claimProblem']>
                labelId="encounter-claim-problem-filter-label"
                label="Claim problem"
                value={filters.claimProblem}
                onChange={event => {
                  const claimProblem = event.target.value;
                  trackClaimProblemChange(claimProblem);
                  updateFilters({ claimProblem });
                }}
              >
                <MenuItem value="">No claim problem filter</MenuItem>
                {claimProblemOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.staleOnly}
                  onChange={event => updateFilters({ staleOnly: event.target.checked })}
                />
              }
              label="Stale only"
            />
            <FormControl size="small" fullWidth sx={drawerFieldSx}>
              <InputLabel id="bill-with-jotpsych-filter-label">Bill with JotPsych</InputLabel>
              <Select<WorklistFilterState['billWithJotpsych']>
                labelId="bill-with-jotpsych-filter-label"
                label="Bill with JotPsych"
                value={filters.billWithJotpsych}
                onChange={event => updateFilters({ billWithJotpsych: event.target.value })}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleResetView}
              disabled={!hasAnyFilter}
              sx={{ textTransform: 'none' }}
            >
              Reset view
            </Button>
            <Button
              variant="contained"
              onClick={() => setFiltersOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          borderColor: ELIG_TOKENS.line,
          overflow: 'hidden',
          width: '100%',
        }}
        aria-busy={isWorklistLoading}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1120 }}>
            <Box
              role="row"
              sx={{
                display: 'grid',
                gridTemplateColumns: worklistGridTemplateColumns,
                columnGap: 1.5,
                alignItems: 'center',
                px: 2,
                py: 1.25,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  onChange={handleToggleAllVisible}
                  disabled={isWorklistLoading || rows.length === 0}
                  sx={{ p: 0.5 }}
                />
              </Box>
              {worklistHeaderCells.map(cell => (
                <Typography
                  key={cell.key}
                  variant="subtitle2"
                  sx={{
                    minWidth: 0,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    textAlign: cell.align ?? 'left',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cell.label}
                </Typography>
              ))}
              <Box />
            </Box>
            {isWorklistLoading ? (
              <WorklistSkeletonRows count={skeletonRowCount} />
            ) : (
              rows.map(row => (
                <EncounterRow
                  key={row.id}
                  row={row}
                  selected={selectedIds.has(row.id)}
                  onOpen={event => handleOpenRow(row, event)}
                  onOpenClaimWorkspace={event => handleOpenClaimWorkspace(row, event)}
                  onToggleSelect={() => handleToggleRow(row.id)}
                  onCoverageGapClick={
                    row.identity.patient_id
                      ? () => handleCoverageGapClick(row.identity.patient_id as string)
                      : undefined
                  }
                />
              ))
            )}
          </Box>
        </Box>
      </Paper>

      {!isWorklistLoading && rows.length === 0 && <Alert severity="info">No encounters match those filters.</Alert>}

      <Pagination
        data={{
          page: data?.page ?? page,
          per_page: data?.per_page ?? filters.perPage,
          has_more: data?.has_more ?? false,
          known_minimum: data?.known_minimum ?? 0,
          count_on_page: data?.items?.length ?? 0,
        }}
        onPageChange={setPage}
        onPerPageChange={handlePerPageChange}
      />
    </Stack>
  );
};

function StageFlowFilter({
  value,
  onChange,
}: {
  value: UnifiedEncounterStage;
  onChange: (key: string) => void;
}) {
  const allSelected = value === 'all';
  const activeColor = '#382299';
  const inactiveColor = '#D9D9E3';

  return (
    <Stack spacing={1}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', minWidth: 0 }}
      >
        <Stack spacing={0.15} sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, lineHeight: 1.2 }}>
            Primary stage filter
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
            All stages shows every encounter. Select one phase to show only encounters whose current work is there.
          </Typography>
        </Stack>
        <Button
          variant={allSelected ? 'contained' : 'outlined'}
          size="small"
          onClick={() => onChange('all')}
          sx={{
            minHeight: 32,
            alignSelf: { xs: 'stretch', sm: 'center' },
            textTransform: 'none',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            All stages view
          </Typography>
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(5, minmax(0, 1fr))',
          },
          gap: 0.75,
          minWidth: 0,
        }}
      >
        {STAGES.map(stage => {
          const isSelected = value === stage.key;
          const isDimmed = !allSelected && !isSelected;
          const segmentColor = allSelected || isSelected ? activeColor : inactiveColor;
          const tooltip = `Show encounters currently in ${stage.filterLabel}`;

          return (
            <Tooltip key={stage.key} title={tooltip}>
              <Button
                variant="text"
                size="small"
                onClick={() => onChange(stage.key)}
                aria-pressed={isSelected}
                sx={{
                  minWidth: 0,
                  minHeight: 42,
                  px: 0,
                  py: 0,
                  textTransform: 'none',
                  color: isSelected ? 'text.primary' : 'text.secondary',
                  borderRadius: 1,
                  bgcolor: 'transparent',
                  opacity: isDimmed ? 0.7 : 1,
                  '&:hover': {
                    bgcolor: 'transparent',
                    opacity: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    gap: 0.6,
                    minWidth: 0,
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      minWidth: 0,
                      lineHeight: 1,
                    }}
                  >
                    {stage.icon}
                    <Typography variant="caption" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {stage.filterLabel}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      bgcolor: segmentColor,
                    }}
                  />
                </Box>
              </Button>
            </Tooltip>
          );
        })}
      </Box>
    </Stack>
  );
}

function WorklistSkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Box
          key={index}
          role="row"
          aria-hidden="true"
          sx={{
            display: 'grid',
            gridTemplateColumns: worklistGridTemplateColumns,
            columnGap: 1.5,
            alignItems: 'center',
            px: 2,
            py: 1.25,
            minHeight: 64,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Skeleton variant="rounded" width={18} height={18} />
          </Box>
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Skeleton variant="text" width="72%" />
            <Skeleton variant="text" width="48%" />
          </Stack>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Skeleton variant="circular" width={24} height={24} />
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="68%" />
                <Skeleton variant="text" width="56%" />
              </Stack>
            </Box>
          </Box>
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Skeleton variant="text" width="74%" />
            <Skeleton variant="text" width="46%" />
          </Stack>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Skeleton variant="circular" width={24} height={24} />
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="64%" />
                <Skeleton variant="text" width="42%" />
              </Stack>
            </Box>
          </Box>
          <Skeleton variant="rounded" width="100%" height={8} />
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="rounded" width={72} height={24} />
              <Skeleton variant="rounded" width={86} height={24} />
            </Box>
            <Skeleton variant="text" width="88%" />
            <Skeleton variant="circular" width={22} height={22} />
          </Stack>
          <Stack spacing={0.5} sx={{ minWidth: 0, alignItems: 'flex-end' }}>
            <Skeleton variant="text" width={72} />
            <Skeleton variant="text" width={56} />
          </Stack>
          <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
        </Box>
      ))}
    </>
  );
}

function MoneyCell({ money }: { money: WorklistMoney }) {
  const chargeSlots = getChargeMoneySlots(money);
  const responsibilitySlots = getPatientResponsibilitySlots(money);
  const statusSlots = getStatusMoneySlots(money);
  const fallbackLabel = getMoneyFallbackLabel(money);

  return (
    <Stack spacing={0.2} sx={{ alignItems: 'flex-end', minWidth: 0, width: 174 }}>
      {chargeSlots.left || chargeSlots.right ? (
        <MoneyLedgerRow left={chargeSlots.left} right={chargeSlots.right} />
      ) : (
        <Typography variant="body2" noWrap title={money.label}>
          {fallbackLabel}
        </Typography>
      )}
      {(responsibilitySlots.left || responsibilitySlots.right) && (
        <MoneyLedgerRow left={responsibilitySlots.left} right={responsibilitySlots.right} />
      )}
      {(statusSlots.left || statusSlots.right) && (
        <MoneyLedgerRow left={statusSlots.left} right={statusSlots.right} subtle />
      )}
    </Stack>
  );
}

function MoneyLedgerRow({
  left,
  right,
  subtle = false,
}: {
  left?: MoneySlotSpec;
  right?: MoneySlotSpec;
  subtle?: boolean;
}) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        columnGap: 0.6,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 6px minmax(0, 1fr)',
        minWidth: 0,
        width: '100%',
      }}
    >
      <MoneySlot slot={left} subtle={subtle} align="right" />
      <Typography
        component="span"
        aria-hidden="true"
        variant="caption"
        sx={{
          color: right ? 'divider' : 'transparent',
          fontWeight: 600,
          lineHeight: 1,
          textAlign: 'center',
        }}
      >
        |
      </Typography>
      <MoneySlot slot={right} subtle={subtle} align="left" />
    </Box>
  );
}

function MoneySlot({
  align,
  slot,
  subtle,
}: {
  align: 'left' | 'right';
  slot?: MoneySlotSpec;
  subtle?: boolean;
}) {
  if (!slot) {
    return <Box aria-hidden="true" sx={{ minWidth: 0 }} />;
  }

  const toneSx = moneyTextToneSx[slot.tone ?? 'default'];

  return (
    <Tooltip title={slot.tooltip}>
      <Box
        component="span"
        aria-label={slot.tooltip}
        sx={{
          alignItems: 'center',
          color: toneSx.color,
          display: 'inline-flex',
          gap: 0.35,
          justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          lineHeight: 1.25,
          minWidth: 0,
          opacity: subtle ? 0.72 : 1,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <MoneyIcon icon={slot.icon} color={toneSx.labelColor} />
        <Typography
          component="span"
          variant="body2"
          sx={{
            color: 'inherit',
            fontWeight: 600,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {slot.value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function MoneyIcon({
  color,
  icon,
}: {
  color: string;
  icon: React.ReactElement;
}) {
  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        color,
        display: 'inline-flex',
        flexShrink: 0,
        fontSize: 13,
        justifyContent: 'center',
        width: 14,
        '& svg': { fontSize: 'inherit' },
      }}
    >
      {icon}
    </Box>
  );
}

function getMoneyFallbackLabel(money: WorklistMoney): string {
  if (money.kind === 'none') return money.label;
  if (typeof money.amount_cents === 'number') {
    return formatCentsCompact(money.amount_cents);
  }
  return money.label.replace(/^Credit\s+/i, '');
}

function getChargeMoneySlots(
  money: WorklistMoney,
): { left?: MoneySlotSpec; right?: MoneySlotSpec } {
  const charged = money.charge?.charged_cents ?? null;
  const expected = money.charge?.expected_cents ?? money.expected_collection_cents ?? null;
  const left: MoneySlotSpec | undefined =
    charged != null && charged > 0
      ? {
          key: 'charged',
          icon: <ReceiptLongIcon fontSize="inherit" />,
          value: formatCentsCompact(charged),
          tone: money.undercharge_risk ? 'warning' : 'default',
          tooltip: `Charged amount: ${formatCents(charged)}.`,
        }
      : undefined;
  const right: MoneySlotSpec | undefined =
    expected != null && expected > 0
      ? {
          key: 'expected',
          icon: <FactCheckIcon fontSize="inherit" />,
          value: formatCentsCompact(expected),
          tone: money.undercharge_risk ? 'warning' : 'default',
          tooltip: [
            `Expected amount: ${formatCents(expected)}.`,
            money.expected_allowed_cents
              ? `Expected allowed: ${formatCents(money.expected_allowed_cents)}.`
              : null,
          ]
            .filter(Boolean)
            .join(' '),
        }
      : undefined;

  return { left, right };
}

function getPatientResponsibilitySlots(
  money: WorklistMoney,
): { left?: MoneySlotSpec; right?: MoneySlotSpec } {
  const responsibility = money.patient_responsibility;
  if (!responsibility) return {};

  const context =
    money.reconciliation?.explanation || money.reconciliation?.headline || undefined;
  const estimated = responsibility.estimated_cents ?? 0;
  const confirmed = responsibility.confirmed_cents ?? 0;
  const collected = responsibility.collected_cents ?? 0;
  const outstanding = responsibility.outstanding_cents ?? 0;
  const credit = responsibility.credit_cents ?? 0;
  const hasConfirmed = confirmed > 0;
  const sourceAmount = hasConfirmed ? confirmed : estimated;
  const sourceLabel = hasConfirmed ? 'confirmed' : estimated > 0 ? 'estimated' : undefined;
  const due = outstanding > 0 ? outstanding : collected === 0 && credit === 0 ? sourceAmount : 0;
  const left =
    due > 0
      ? {
          key: 'due-responsibility',
          icon: <PersonIcon fontSize="inherit" />,
          value: formatCentsCompact(due),
          tone: 'default' as MoneyTextTone,
          tooltip: withTooltipContext(
            [
              `Patient due: ${formatCents(due)}.`,
              sourceLabel === 'confirmed'
                ? `Based on confirmed responsibility of ${formatCents(confirmed)}.`
                : null,
              sourceLabel === 'estimated'
                ? `Based on estimated responsibility of ${formatCents(estimated)}.`
                : null,
            ]
              .filter(Boolean)
              .join(' '),
            context,
          ),
        }
      : undefined;
  const right =
    collected > 0
      ? {
          key: 'collected-responsibility',
          icon: <PaidOutlinedIcon fontSize="inherit" />,
          value: formatCentsCompact(collected),
          tone: 'muted' as MoneyTextTone,
          tooltip: withTooltipContext(
            `Collected from patient: ${formatCents(collected)}.`,
            context,
          ),
        }
      : credit > 0
        ? {
            key: 'credit-responsibility',
            icon: <PaidOutlinedIcon fontSize="inherit" />,
            value: formatCentsCompact(credit),
            tone: 'muted' as MoneyTextTone,
            tooltip: withTooltipContext(
              `Patient credit: ${formatCents(credit)}.`,
              context,
            ),
          }
        : undefined;

  return { left, right };
}

function getStatusMoneySlots(
  money: WorklistMoney,
): { left?: MoneySlotSpec; right?: MoneySlotSpec } {
  let left: MoneySlotSpec | undefined;
  let right: MoneySlotSpec | undefined;
  const reconciliation = money.reconciliation;
  const secondary = money.secondary;

  if (money.undercharge_risk) {
    left = {
      key: 'undercharge-risk',
      icon: <WarningAmberIcon fontSize="inherit" />,
      value: '',
      tone: 'warning',
      tooltip: [
        'Charge is below the expected allowed amount.',
        money.expected_allowed_cents
          ? `Expected allowed: ${formatCents(money.expected_allowed_cents)}.`
          : null,
      ]
        .filter(Boolean)
        .join(' '),
    };
  }

  if (
    (money.kind === 'written_off' || money.kind === 'patient_paid') &&
    typeof money.amount_cents === 'number'
  ) {
    const isPatientPaid = money.kind === 'patient_paid';
    right = {
      key: money.kind,
      icon: isPatientPaid ? <PaidOutlinedIcon fontSize="inherit" /> : <TaskAltIcon fontSize="inherit" />,
      value: formatCentsCompact(money.amount_cents),
      tone: 'success',
      tooltip: `${isPatientPaid ? 'Patient paid' : 'Written off'}: ${formatCents(money.amount_cents)}.`,
    };
  }

  if (reconciliation) {
    const reconciliationSlots = getReconciliationStatusSlots(reconciliation);
    left = left ?? reconciliationSlots.left;
    right = reconciliationSlots.right;
  } else if (secondary && secondary.kind !== 'expected_collection') {
    right = getSecondaryMoneySlot(secondary);
  }

  return { left, right };
}

function getReconciliationStatusSlots(
  reconciliation: WorklistMoneyReconciliation,
): { left?: MoneySlotSpec; right?: MoneySlotSpec } {
  let left: MoneySlotSpec | undefined;
  let right: MoneySlotSpec | undefined;
  const context = reconciliation.explanation || reconciliation.headline || undefined;

  if (reconciliation.state === 'secondary_pending_suspended') {
    left = {
      key: 'secondary-pending',
      icon: <PendingActionsOutlinedIcon fontSize="inherit" />,
      value: '',
      tone: 'info',
      tooltip: withTooltipContext('Secondary pending: do not bill patient yet.', context),
    };
  }

  if (reconciliation.state === 'denial_or_claim_review') {
    left = {
      key: 'claim-review',
      icon: <WarningAmberIcon fontSize="inherit" />,
      value: '',
      tone: 'error',
      tooltip: withTooltipContext(
        'Claim review needed before patient collection.',
        context,
      ),
    };
  }

  if ((reconciliation.active_invoice_cents ?? 0) > 0) {
    const isStaleInvoice =
      reconciliation.state === 'stale_invoice_void' ||
      reconciliation.state === 'stale_invoice_reissue';
    right = {
      key: 'active-invoice',
      icon: <ReceiptLongIcon fontSize="inherit" />,
      value: formatCentsCompact(reconciliation.active_invoice_cents),
      tone: isStaleInvoice ? 'warning' : 'muted',
      tooltip: withTooltipContext(
        `${isStaleInvoice ? 'Stale invoice' : 'Open invoice'}: ${formatCents(reconciliation.active_invoice_cents)}.`,
        context,
      ),
    };
  }

  return { left, right };
}

function getSecondaryMoneySlot(secondary: WorklistMoneySecondary): MoneySlotSpec {
  if (secondary.kind === 'expected_collection') {
    return {
      key: 'expected-collection',
      icon: <FactCheckIcon fontSize="inherit" />,
      value: formatCentsCompact(secondary.amount_cents),
      tone: 'default',
      tooltip: `Expected collection: ${formatCents(secondary.amount_cents)}.`,
    };
  }

  if (secondary.kind === 'open_invoice' || secondary.kind === 'stale_invoice') {
    const isStaleInvoice = secondary.kind === 'stale_invoice';
    return {
      key: secondary.kind,
      icon: <ReceiptLongIcon fontSize="inherit" />,
      value: formatCentsCompact(secondary.amount_cents),
      tone: isStaleInvoice ? 'warning' : 'muted',
      tooltip: `${isStaleInvoice ? 'Stale invoice' : 'Open invoice'}: ${formatCents(secondary.amount_cents)}.`,
    };
  }

  if (secondary.kind === 'responsibility_reconciliation') {
    return {
      key: secondary.kind,
      icon: <TaskAltIcon fontSize="inherit" />,
      value: '',
      tone: 'info',
      tooltip: secondary.label,
    };
  }

  return {
    key: secondary.kind,
    icon: <ReceiptLongIcon fontSize="inherit" />,
    value: '',
    tone: 'muted',
    tooltip: secondary.label,
  };
}

function withTooltipContext(label: string, context?: string): string {
  return context ? `${label} ${context}` : label;
}

function EncounterRow({
  row,
  selected,
  onOpen,
  onOpenClaimWorkspace,
  onToggleSelect,
  onCoverageGapClick,
}: {
  row: UnifiedEncounterWorklistItem;
  selected: boolean;
  onOpen: (event?: RowOpenEvent) => void;
  onOpenClaimWorkspace: (event?: RowOpenEvent) => void;
  onToggleSelect: () => void;
  onCoverageGapClick?: () => void;
}) {
  const treatment = getIndicatorTreatment(rowToStageState(row));
  const workActionText = worklistActionText(row);
  const displayOwner = worklistDisplayOwner(row);

  return (
    <Box
      tabIndex={0}
      role="button"
      aria-label={`Open unified encounter page for ${row.identity.patient_name}`}
      onClick={onOpen}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: worklistGridTemplateColumns,
        columnGap: 1.5,
        alignItems: 'center',
        px: 2,
        py: 1.25,
        minHeight: 64,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        transition: theme =>
          theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shorter,
          }),
        '&:hover': {
          bgcolor: alpha(ELIG_TOKENS.midnight, 0.02),
        },
        '&:focus-visible': {
          outline: theme => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Checkbox
          size="small"
          checked={selected}
          onClick={event => event.stopPropagation()}
          onChange={event => {
            event.stopPropagation();
            onToggleSelect();
          }}
          sx={{ p: 0.5 }}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
            <EncounterIdentityBadges
              badges={row.identity.badges}
              size={14}
              onCoverageGapClick={onCoverageGapClick}
            />
            <Typography variant="body2" noWrap>
              {formatServiceDate(row.identity.service_date)}
            </Typography>
          </Stack>
          <Tooltip title={formatLastActivityTooltip(row)} placement="top" arrow>
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatLastActivityTime(row)}
            </Typography>
          </Tooltip>
        </Stack>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Avatar
            id={row.identity.patient_id ?? row.identity.patient_name}
            {...getNameParts(row.identity.patient_name)}
            sx={{ width: 24, height: 24, fontSize: '0.625rem', fontWeight: 600, flexShrink: 0 }}
          />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.identity.patient_name}>
              {row.identity.patient_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={row.identity.payer_name}>
              {row.identity.payer_name}
            </Typography>
          </Stack>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack spacing={0.25}>
          <Typography variant="body2" noWrap title={row.identity.service_label}>
            {row.identity.service_label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={row.identity.billing_codes_label || undefined}
          >
            {formatBillingCodesLabel(row.identity.billing_codes_label)}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <ProviderBadge name={row.identity.provider_name} size="regular" />
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.identity.provider_name}>
              {row.identity.provider_name}
            </Typography>
            {row.identity.provider_license_label && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={row.identity.provider_license_label}
              >
                {row.identity.provider_license_label}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <StageStrip row={row} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <ActionMark
              row={row}
              onOpenClaimWorkspace={onOpenClaimWorkspace}
            />
            {displayOwner !== 'none' && (
              <Chip
                label={ownerLabel(displayOwner)}
                color={ownerColor[displayOwner] ?? 'default'}
                variant="soft"
                size="small"
              />
            )}
          </Stack>
          <Tooltip title={workActionText}>
            <Typography
              variant="body2"
              color={treatment.actionNeeded ? 'text.primary' : 'text.secondary'}
              noWrap
              sx={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {workActionText}
            </Typography>
          </Tooltip>
          <RowClaimLineage row={row} />
        </Stack>
      </Box>
      <Box sx={{ minWidth: 0, textAlign: 'right' }}>
        <MoneyCell money={row.money} />
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
        <EncounterRowActions row={row} onOpen={onOpen} />
      </Box>
    </Box>
  );
}

function RowClaimLineage({ row }: { row: UnifiedEncounterWorklistItem }) {
  const claimId = row.claim.id;
  if (!claimId) return null;

  const type = row.claim.claim_type;
  const isCorrection = type === 'corrected' || row.claim.frequency_code === '7';
  const isVoid = type === 'void' || row.claim.frequency_code === '8';
  const isSuperseded = Boolean(row.claim.superseded_by_claim_id);
  if (!isCorrection && !isVoid && !isSuperseded) return null;

  const lineage = isVoid
    ? {
        label: 'Void claim',
        icon: <BlockIcon fontSize="inherit" />,
        color: 'error.main',
        backgroundColor: 'error.lighter',
      }
    : isCorrection
      ? {
          label: 'Corrected claim',
          icon: <ReplayIcon fontSize="inherit" />,
          color: 'info.main',
          backgroundColor: 'info.lighter',
        }
      : {
          label: 'Superseded by a later claim',
          icon: <CallSplitIcon fontSize="inherit" />,
          color: 'warning.main',
          backgroundColor: 'warning.lighter',
        };

  return (
    <Tooltip title={lineage.label}>
      <Box
        component="span"
        aria-label={lineage.label}
        sx={{
          alignItems: 'center',
          backgroundColor: lineage.backgroundColor,
          borderRadius: '50%',
          color: lineage.color,
          display: 'inline-flex',
          fontSize: 14,
          height: 22,
          justifyContent: 'center',
          width: 22,
        }}
      >
        {lineage.icon}
      </Box>
    </Tooltip>
  );
}

function EncounterRowActions({
  row,
  onOpen,
}: {
  row: UnifiedEncounterWorklistItem;
  onOpen: (event?: RowOpenEvent) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Disposition acts on the encounter's claim. Pre-claim encounters have no
  // claim id, so the Dismiss / "Remind me tomorrow" items are hidden there.
  // We gate on claim presence rather than `canDispositionClaim` (used by the
  // claims list): the worklist row's claim object is minimal and carries no
  // `era_triage` / `phase` / `error_count`, and the /attention endpoint accepts
  // a dismiss/snooze for any claim — so presence is the right gate here.
  const claimId = row.claim?.id ?? undefined;
  const { dismiss, remindTomorrow, isUpdating } = useClaimDisposition({
    source: 'encounter_worklist',
  });

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOpenEncounter = useCallback(() => {
    handleCloseMenu();
    onOpen();
  }, [handleCloseMenu, onOpen]);

  const handleRemindTomorrow = useCallback(() => {
    handleCloseMenu();
    if (claimId) void remindTomorrow(claimId);
  }, [handleCloseMenu, remindTomorrow, claimId]);

  const handleDismiss = useCallback(() => {
    handleCloseMenu();
    if (claimId) void dismiss(claimId);
  }, [handleCloseMenu, dismiss, claimId]);

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Actions for ${row.identity.patient_name}`}
        onClick={handleOpenMenu}
        sx={{ p: 0.5 }}
      >
        {isUpdating ? <CircularProgress size={16} /> : <MoreVertIcon sx={{ fontSize: 18 }} />}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        onClick={event => event.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: { sx: { minWidth: 180, mt: 0.5 } },
        }}
      >
        <MenuItem onClick={handleOpenEncounter}>
          <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
            Open encounter
          </ListItemText>
        </MenuItem>
        {claimId && [
          <Divider key="div-disposition" />,
          <MenuItem key="remind-tomorrow" onClick={handleRemindTomorrow} disabled={isUpdating}>
            <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Remind me tomorrow
            </ListItemText>
          </MenuItem>,
          <MenuItem key="dismiss" onClick={handleDismiss} disabled={isUpdating}>
            <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
              Dismiss
            </ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
}

function formatBillingCodesLabel(value?: string | null) {
  const codes = (value ?? '')
    .split(',')
    .map(code => code.trim().replace(/^(?:CPT|HCPCS):/i, ''))
    .filter(Boolean);
  if (!codes.length) return 'No billing codes';
  const additionalCount = codes.length - 1;
  return additionalCount > 0 ? `${codes[0]} +${additionalCount}` : codes[0];
}

function getNameParts(name?: string | null): { firstName: string; lastName: string } {
  if (!name) return { firstName: '', lastName: '' };
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function StageStrip({ row, height = 8 }: { row: UnifiedEncounterWorklistItem; height?: number }) {
  const currentIndex = stageRank[row.flow.active_stage] ?? 0;
  const currentTreatment = getIndicatorTreatment(rowToStageState(row));
  const currentColor = toneColor[currentTreatment.tone];
  const stageByKey = new Map(row.flow.stages.map(stage => [stage.key, stage]));

  return (
    <Box
      role="img"
      aria-label={`${row.identity.patient_name} is in ${row.flow.active_stage}; ${currentTreatment.label}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 0.5,
        width: '100%',
        minWidth: 120,
      }}
    >
      {STAGES.map((stage, index) => {
        const flowStage = stageByKey.get(stage.key);
        const stageTreatment = getIndicatorTreatment(flowStateToStageState(flowStage?.state));
        const isCurrent = index === currentIndex;
        const isComplete = index < currentIndex;
        const isFuture = index > currentIndex;
        const segmentColor = isCurrent
          ? currentColor
          : isComplete
            ? ELIG_TOKENS.green
            : ELIG_TOKENS.gray;
        const tooltip = `${stage.label}: ${stageTreatment.label}${
          flowStage?.summary ? ` - ${flowStage.summary}` : ''
        }`;
        const currentActionOutline = isCurrent && currentTreatment.actionNeeded && !currentTreatment.filled;

        return (
          <Tooltip key={stage.key} title={tooltip}>
            <Box
              aria-label={tooltip}
              role="img"
              sx={{
                height,
                borderRadius: 999,
                boxSizing: 'border-box',
                bgcolor:
                  currentActionOutline
                    ? 'transparent'
                    : isFuture
                      ? ELIG_TOKENS.graySoft
                      : segmentColor,
                border:
                  currentActionOutline
                    ? `${Math.max(2, height / 3)}px solid ${segmentColor}`
                    : 'none',
                opacity: isComplete ? 0.34 : isFuture ? 0.52 : 1,
                cursor: 'help',
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function ActionMark({
  row,
  onOpenClaimWorkspace,
}: {
  row: UnifiedEncounterWorklistItem;
  onOpenClaimWorkspace: (event?: RowOpenEvent) => void;
}) {
  const treatment = getIndicatorTreatment(rowToStageState(row));
  const color = toneColor[treatment.tone];
  const markerSize = treatment.filled ? 12 : 11;
  const opensClaimWorkspace = isFixSignal(row);
  const tooltipTitle = opensClaimWorkspace
    ? 'Open claim tab'
    : worklistActionText(row);

  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minHeight: 20 }}>
      <Box sx={{ width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Box
          sx={{
            width: markerSize,
            height: markerSize,
            borderRadius: '50%',
            boxSizing: 'border-box',
            bgcolor: treatment.filled ? color : 'transparent',
            border: treatment.filled ? 'none' : `2px solid ${color}`,
            boxShadow:
              treatment.tone === 'red' && treatment.filled
                ? `0 0 0 2px ${alpha(color, 0.16)}`
                : 'none',
          }}
        />
      </Box>
      <Tooltip title={tooltipTitle}>
        <Chip
          label={row.signal.label}
          color={signalChipColor(row)}
          variant="soft"
          size="small"
          aria-label={
            opensClaimWorkspace
              ? `Open claim tab for ${row.identity.patient_name}`
              : undefined
          }
          onClick={
            opensClaimWorkspace
              ? event => {
                  event.stopPropagation();
                  onOpenClaimWorkspace(event);
                }
              : undefined
          }
          sx={{ cursor: opensClaimWorkspace ? 'pointer' : 'inherit', fontWeight: 700 }}
        />
      </Tooltip>
    </Stack>
  );
}

function getClaimWorkspaceHref(row: UnifiedEncounterWorklistItem): string {
  return withWorkspaceParam(row.actions.claim_href ?? row.actions.primary_href, 'claim');
}

function withWorkspaceParam(href: string, workspace: 'overview' | 'claim' | 'payments'): string {
  const hashIndex = href.indexOf('#');
  const beforeHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
  const queryIndex = beforeHash.indexOf('?');
  const path = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : '';
  const params = new URLSearchParams(query);
  params.set('workspace', workspace);

  return `${path}?${params.toString()}${hash}`;
}

function isFixSignal(row: UnifiedEncounterWorklistItem): boolean {
  return /^fix\b/i.test(row.signal.label.trim());
}

function getIndicatorTreatment(state: StageState) {
  if (state === 'blocked') {
    return { actionNeeded: true, filled: true, label: 'Blocked', tone: 'red' as const };
  }
  if (state === 'ready') {
    return { actionNeeded: true, filled: false, label: 'Ready', tone: 'amber' as const };
  }
  if (state === 'complete') {
    return { actionNeeded: false, filled: true, label: 'Complete', tone: 'green' as const };
  }
  if (state === 'waiting') {
    return { actionNeeded: false, filled: false, label: 'Waiting', tone: 'gray' as const };
  }
  if (state === 'current') {
    return { actionNeeded: false, filled: true, label: 'Current', tone: 'midnight' as const };
  }
  return { actionNeeded: false, filled: false, label: 'Future', tone: 'gray' as const };
}

function signalChipColor(row: UnifiedEncounterWorklistItem): ChipColor {
  if (row.signal.work_state === 'clear') return 'success';
  if (row.signal.severity === 'error') return 'error';
  return signalColor[row.signal.work_state];
}

function rowToStageState(row: UnifiedEncounterWorklistItem): StageState {
  if (row.signal.work_state === 'action') {
    return row.signal.severity === 'error' ? 'blocked' : 'ready';
  }
  if (row.signal.work_state === 'waiting') return 'waiting';
  return flowStateToStageState(row.flow.active_state);
}

function flowStateToStageState(state?: string): StageState {
  if (state === 'complete' || state === 'terminal') return 'complete';
  if (state === 'waiting') return 'waiting';
  if (state === 'blocked') return 'blocked';
  if (state === 'action_needed') return 'ready';
  if (state === 'open') return 'current';
  return 'future';
}

function ownerLabel(owner: string): string {
  const labels: Record<string, string> = {
    front_desk: 'Front desk',
    clinician: 'Clinician',
    biller: 'Biller',
    billing: 'Biller',
    payer: 'Payer',
    patient: 'Patient',
    none: 'None',
  };
  return labels[owner] ?? owner;
}

function worklistDisplayOwner(row: UnifiedEncounterWorklistItem): string {
  return row.signal.label === 'Send invoice' ? 'biller' : row.signal.owner;
}

function worklistActionText(row: UnifiedEncounterWorklistItem): string {
  if (row.signal.work_state === 'clear') {
    return row.signal.detail || row.signal.label;
  }
  return row.signal.detail || row.flow.primary_action?.label || row.signal.label;
}

function formatServiceDate(value?: string | null): string {
  if (!value) return 'Unknown';
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatLastActivityTime(row: UnifiedEncounterWorklistItem): string {
  const activity = row.last_activity;
  if (!activity) return row.age.label;
  return activity.relative_label || row.age.label;
}

function formatLastActivityTooltip(row: UnifiedEncounterWorklistItem): string {
  const activity = row.last_activity;
  if (!activity) return row.age.label;
  const parts = [activity.label];
  const exactTime = formatActivityTimestamp(activity.at);
  if (exactTime) parts.push(exactTime);
  return parts.filter(Boolean).join(' · ');
}

function formatActivityTimestamp(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function toISODate(date: Date | null): string | undefined {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getPatientDisplayName(patient: Patient): string {
  return [patient.first_name, patient.last_name].filter(Boolean).join(' ') || 'Selected patient';
}

function getPersistedPatientOption(filters: WorklistFilterState): PatientInfo | undefined {
  if (!filters.patientId) return undefined;
  return {
    patient_id: filters.patientId,
    name: filters.patientLabel || 'Selected patient',
  };
}

type PersistedWorklistFilters = Partial<
  Omit<WorklistFilterState, 'dateRange'> & {
    dateStartISO?: string | null;
    dateEndISO?: string | null;
    /** Removed in GDN-2770; older payloads still carry it. */
    severity?: unknown;
  }
>;

function loadWorklistFilters(): WorklistFilterState {
  if (typeof window === 'undefined') return defaultWorklistFilters;
  try {
    const raw = window.localStorage.getItem(worklistFilterStorageKey);
    if (!raw) return defaultWorklistFilters;
    // The severity filter was retired in GDN-2770. Drop it here so a payload
    // written by an older session neither crashes nor keeps filtering, and so
    // the next save rewrites storage without it.
    const { severity: _removedSeverity, ...parsed } = JSON.parse(
      raw,
    ) as PersistedWorklistFilters;

    return {
      ...defaultWorklistFilters,
      ...parsed,
      nextStep: isNextStep(parsed.nextStep) ? parsed.nextStep : defaultWorklistFilters.nextStep,
      claimProblem: isClaimProblem(parsed.claimProblem)
        ? parsed.claimProblem
        : defaultWorklistFilters.claimProblem,
      sortBy: isSortBy(parsed.sortBy) ? parsed.sortBy : defaultWorklistFilters.sortBy,
      sortOrder: parsed.sortOrder === 'asc' || parsed.sortOrder === 'desc'
        ? parsed.sortOrder
        : defaultWorklistFilters.sortOrder,
      providerIds: Array.isArray(parsed.providerIds) ? parsed.providerIds : [],
      dateRange: {
        start: parsed.dateStartISO ? new Date(parsed.dateStartISO) : null,
        end: parsed.dateEndISO ? new Date(parsed.dateEndISO) : null,
      },
    };
  } catch {
    return defaultWorklistFilters;
  }
}

function isSortBy(value: unknown): value is SortBy {
  return value === 'dos' || value === 'activity';
}

function isNextStep(value: unknown): value is WorklistFilterState['nextStep'] {
  return value === '' || (typeof value === 'string' && value in nextStepLabels);
}

function isClaimProblem(value: unknown): value is WorklistFilterState['claimProblem'] {
  return value === '' || (typeof value === 'string' && value in claimProblemLabels);
}

function toSortPreset(filters: Pick<WorklistFilterState, 'sortBy' | 'sortOrder'>): SortPreset {
  const candidate = `${filters.sortBy}:${filters.sortOrder}`;
  return isSortPreset(candidate) ? candidate : defaultSortPreset;
}

function isSortPreset(value: string): value is SortPreset {
  return sortOptions.some(option => option.value === value);
}

function saveWorklistFilters(filters: WorklistFilterState): void {
  if (typeof window === 'undefined') return;
  try {
    const { dateRange, ...rest } = filters;
    window.localStorage.setItem(
      worklistFilterStorageKey,
      JSON.stringify({
        ...rest,
        dateStartISO: dateRange.start?.toISOString() ?? null,
        dateEndISO: dateRange.end?.toISOString() ?? null,
      }),
    );
  } catch {
    // Ignore storage errors.
  }
}

function getAdvancedFilterCount(filters: WorklistFilterState): number {
  return [
    filters.patientId,
    filters.providerIds.length > 0,
    filters.payerUuid,
    filters.owner,
    filters.nextStep,
    filters.claimProblem,
    filters.claimFilter !== 'any',
    filters.staleOnly,
    filters.billWithJotpsych !== '',
  ].filter(Boolean).length;
}

function hasNonDefaultFilters(filters: WorklistFilterState): boolean {
  return (
    filters.workState !== defaultWorklistFilters.workState ||
    filters.stage !== defaultWorklistFilters.stage ||
    filters.sortBy !== defaultWorklistFilters.sortBy ||
    filters.sortOrder !== defaultWorklistFilters.sortOrder ||
    filters.perPage !== defaultWorklistFilters.perPage ||
    Boolean(filters.dateRange.start || filters.dateRange.end) ||
    getAdvancedFilterCount(filters) > 0
  );
}

function getAdvancedFilterChips(
  filters: WorklistFilterState,
  clinicians: Clinician[],
): Array<{
  key: string;
  label: string;
  onDelete: (update: (patch: Partial<WorklistFilterState>) => void) => void;
}> {
  const chips: Array<{
    key: string;
    label: string;
    onDelete: (update: (patch: Partial<WorklistFilterState>) => void) => void;
  }> = [];

  if (filters.patientId) {
    chips.push({
      key: 'patient',
      label: `Patient: ${filters.patientLabel || filters.patientId}`,
      onDelete: update => update({ patientId: '', patientLabel: '' }),
    });
  }

  filters.providerIds.forEach(providerId => {
    const provider = clinicians.find(clinician => clinician.userId === providerId);
    chips.push({
      key: `provider-${providerId}`,
      label: `Provider: ${provider?.name ?? providerId}`,
      onDelete: update =>
        update({ providerIds: filters.providerIds.filter(id => id !== providerId) }),
    });
  });

  if (filters.payerUuid) {
    chips.push({
      key: 'payer',
      label: `Payer: ${filters.payerName || filters.payerUuid}`,
      onDelete: update => update({ payerUuid: '', payerName: '' }),
    });
  }

  if (filters.owner) {
    chips.push({
      key: 'owner',
      label: `Owner: ${ownerLabel(filters.owner)}`,
      onDelete: update => update({ owner: '' }),
    });
  }

  if (filters.nextStep) {
    chips.push({
      key: 'nextStep',
      label: `Next step: ${nextStepLabels[filters.nextStep]}`,
      onDelete: update => update({ nextStep: '' }),
    });
  }

  if (filters.claimProblem) {
    chips.push({
      key: 'claimProblem',
      label: `Claim problem: ${claimProblemLabels[filters.claimProblem]}`,
      onDelete: update => update({ claimProblem: '' }),
    });
  }

  if (filters.claimFilter !== 'any') {
    chips.push({
      key: 'claim',
      label: filters.claimFilter === 'has_claim' ? 'Has claim' : 'No claim',
      onDelete: update => update({ claimFilter: 'any' }),
    });
  }

  if (filters.staleOnly) {
    chips.push({
      key: 'stale',
      label: 'Stale only',
      onDelete: update => update({ staleOnly: false }),
    });
  }

  if (filters.billWithJotpsych) {
    chips.push({
      key: 'billWithJotpsych',
      label: `JotPsych billing: ${filters.billWithJotpsych === 'yes' ? 'Yes' : 'No'}`,
      onDelete: update => update({ billWithJotpsych: '' }),
    });
  }

  return chips;
}

const emptyWorkSegments: UnifiedEncounterWorklistSegment[] = [
  { key: 'all', label: 'All', severity: 'info' },
  { key: 'action', label: 'Action', severity: 'warning' },
  { key: 'waiting', label: 'Waiting', severity: 'info' },
  { key: 'clear', label: 'Clear', severity: 'success' },
];
